import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

/**
 * Fallback UI rendered by react-error-boundary when a child component throws.
 *
 * Goals:
 *   - Never show a blank white page. Always give the user a way out.
 *   - Show enough detail in development to debug, nothing scary in
 *     production.
 *   - Make "Reload" the obvious action — most production errors clear
 *     on a fresh page load (stale module after deploy, transient JSON
 *     parse, etc.).
 *
 * Used in two places:
 *   1. At the top of App.jsx, wrapping the whole router. Catches errors
 *      that would otherwise crash the entire SPA.
 *   2. Per-route around the 5 highest-traffic pages (Dashboard,
 *      ExclusiveDashboard, NonExclusiveBookingsDashboard, LeadsDashboard,
 *      VillaDetail) so a bug on one page doesn't lock the user out of
 *      the rest of the app.
 *
 * The `resetErrorBoundary` callback re-renders the boundary's children.
 * Combined with a "Reload page" button (window.location.reload), the
 * user can either retry in-place or do a hard reload.
 */
const isDev = import.meta.env.DEV

export default function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div role="alert" className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-full bg-red-100 p-2 flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-[#1e2a2e]">
              Something went wrong
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              The app hit an unexpected error. Reload the page to recover.
              If this keeps happening, let Oliver know.
            </p>
          </div>
        </div>

        {/* In dev: show the actual error so we can debug. In prod: hide
            the technical details — they leak internal info and don't help
            the user. */}
        {isDev && error && (
          <details className="mb-4 text-xs">
            <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
              Error details (dev only)
            </summary>
            <pre className="mt-2 bg-slate-100 border border-slate-200 rounded p-3 overflow-auto text-slate-800 whitespace-pre-wrap break-words">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 bg-[#1e2a2e] text-white rounded-lg hover:bg-[#2a3a3f] transition-colors font-medium text-sm"
          >
            <RefreshCw size={14} />
            Reload page
          </button>
          <button
            onClick={resetErrorBoundary}
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  )
}
